'use client';

import type { FC, PropsWithChildren } from 'react';
import { getConfig } from '@/utils/config/config';
import { RewardsMachineModalFactory, type RewardsConfig } from '@fanduel/rewards-hub-client';
import { RichText } from '../../rich-text/rich-text';
import { classes } from '@/utils/classes/classes';
import { ROUTES } from '@/utils/constants';
import { RewardMachineGameUrlComponent } from '../subcomponents/reward-machine-game-url';
import './styles/default.scss';
import { CloseButton } from '../../popup/subcomponents/close-button';
import Link from 'next/link';
import { useStore } from '@/store/store';
import { RouteLoadingSpinner } from '../../loading-spinner';
import { type RewardMachineInfoScreenData } from '@/lib/reward-machine-data';
import { getRewardsApiUrlFromAppConfig } from '@/logic/rewards-hub/utils/get-rewards-api-url';

type RewardMachineWelcomeModalPayload = {
    welcomeModalData?: RewardMachineInfoScreenData;
};

type RewardMachineWelcomeModalProps = {
    params?: RewardMachineWelcomeModalPayload;
};

type NotificationBoxProps = PropsWithChildren<{
    title?: string;
    wrapperClass?: string;
    headerClass?: string;
    experienceId?: string;
    footerClass?: string;
}>;

const NotificationBox: FC<NotificationBoxProps> = ({
    children,
    title,
    wrapperClass,
    headerClass,
    experienceId,
    footerClass
}) => {
    const { closePopup } = useStore((state) => state.popup);

    return (
        <div id={experienceId} className={classes('games-modal-rm', wrapperClass)}>
            <div className="games-container">
                <div className={classes('header', headerClass)}>
                    <div className="title">{title}</div>
                    <CloseButton />
                </div>
                <div className="body">{children}</div>
                <div className={classes('footer', footerClass)}>
                    <Link href={ROUTES.REWARD_MACHINE_INFO} className="tc-link" onClick={closePopup}>
                        Terms & Conditions
                    </Link>
                </div>
            </div>
        </div>
    );
};

export const RewardMachineWelcomeModal = ({ params }: RewardMachineWelcomeModalProps) => {
    const providedData = params?.welcomeModalData;

    const { closePopup } = useStore((state) => state.popup);

    const handleClose = () => {
        closePopup();
    };

    const themeName = providedData?.rewardsMachineContent?.data?.rewardsMachineTheme?.theme;

    if (!providedData) {
        return (
            <div>
                <RouteLoadingSpinner />
            </div>
        );
    }

    const apiConfig = getConfig();
    const rewardsApiUrlForConfig =
        providedData.rewardsApiUrl && providedData.rewardsApiUrl.length > 0
            ? providedData.rewardsApiUrl
            : getRewardsApiUrlFromAppConfig(apiConfig);

    const { rewardsApiUrl: _rewardsApiUrlFromPayload, ...modalPayload } = providedData;

    const config = {
        features: providedData.features || [],
        brand: providedData.brand,
        rewardsApiUrl: rewardsApiUrlForConfig
    } as RewardsConfig;

    return (
        <RewardsMachineModalFactory
            {...modalPayload}
            bonusDraw={providedData.bonusDraw ?? ''}
            config={config}
            // TODO: why? it should be on homepage? But i've seen it on other pages as well
            // we sent it true always the logic si handled by popup factory
            displayWelcome={true}
            themeName={themeName}
            GameLauncher={() => (
                <RewardMachineGameUrlComponent
                    shouldTriggerModal={false}
                    disableExitScreenRedirect={true}
                    rgi={'incentive_games'}
                    providerName={'Incentive_Games'}
                    theme={themeName}
                    gameName="Rewards Machine"
                    onClick={handleClose}>
                    <span>Play now</span>
                </RewardMachineGameUrlComponent>
            )}
            NotificationBoxComponent={NotificationBox}
            RichText={RichText}
        />
    );
};
